import type { ReactElement } from 'react';
import { Alert } from '@csa/ui';
import type { SodAnalysisView } from './types.js';

/**
 * Renders segregation-of-duties findings.
 *
 * The rationale is always shown, never collapsed behind a "learn more". An
 * administrator refused a permission combination needs to know *why* in the
 * same glance, or the refusal reads as an arbitrary bug and the next step is a
 * ticket asking for it to be removed.
 */
export function SodFindings({
  analysis,
  context,
}: {
  analysis: SodAnalysisView;
  context: 'role' | 'user';
}): ReactElement | null {
  if (analysis.clean) return null;

  const subject = context === 'role' ? 'This role' : 'This user';

  return (
    <div className="flex flex-col gap-3">
      {analysis.blocking.length > 0 ? (
        <Alert tone="danger" title="Segregation of duties conflict">
          <p>
            {subject}{' '}
            {context === 'role'
              ? 'cannot hold these permissions together.'
              : 'holds these permissions together across their roles. Remove one of the assignments.'}
          </p>
          <ul className="mt-2 flex flex-col gap-2">
            {analysis.blocking.map((finding) => (
              <li key={`${finding.a}:${finding.b}`}>
                <span className="font-medium">
                  {finding.aLabel} + {finding.bLabel}
                </span>
                <span className="block text-muted">{finding.rationale}</span>
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {analysis.warnings.length > 0 ? (
        <Alert tone="warning" title="Concentrated privilege">
          <p>
            {subject} combines duties that are normally separated. This is permitted with a recorded
            justification and is listed in the access review.
          </p>
          <ul className="mt-2 flex flex-col gap-2">
            {analysis.warnings.map((finding) => (
              <li key={`${finding.a}:${finding.b}`}>
                <span className="font-medium">
                  {finding.aLabel} + {finding.bLabel}
                </span>
                <span className="block text-muted">{finding.rationale}</span>
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
    </div>
  );
}
